import PageTitle from "../../../components/PageTitle";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { httprequest } from "../../../data/api";
import TabContext from "@mui/lab/TabContext";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import useStyles from "../styles";
import { Icon } from "@iconify/react/dist/iconify.js";
import Calendar from "../../../components/Calendar";
import useWindowSize from "../../../hooks/useWindow";
import PopModal from "../../../components/PopupModal/PopModal";

const CLASS_ENDPOINT = "/api/v1/teacher/classes";
const TEACHER_PERIOD = "/api/v1/period/teacher";
const TERM_ENDPOINT = "/api/v1/school-plan/terms";

const AdminTimeTable = () => {
  const [classData, setClassData] = useState([]);
  const classes = useStyles();
  // const navigate = useNavigate();
  const [activeTerm, setActiveTerm] = useState([]);
  const [periodData, setPeriodData] = useState([]);
  const [value, setValue] = useState("");
  const [isClassLoading, setIsClassLoading] = useState(false);
  const [timeConfig, setTimeConfig] = useState({
    slotMinTime: "07:00:00",
    slotMaxTime: "19:00:00"
  });

  const width = useWindowSize();
  const mobile = width < 576;

  /**
 * Determines the calendar view mode based on screen size.
 * @returns {string} Returns "timeGridDay" for mobile and "timeGridWeek" for desktop.
 */
  const renderView = () => {
    if (mobile) {
      return "timeGridDay";
    } else {
      return "timeGridWeek";
    }
  };

  /**
   * handle Get all Terms
   */
  const getAllTerms = () => {
    httprequest
      .get(TERM_ENDPOINT)
      .then((res) => {
        setActiveTerm(
          res?.data?.data.find(
            (active_term) => active_term?.is_current === true
          )
        );
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
 * Fetches all classes from the API and updates the class state.
 */
  const getAllClasses = () => {
    setIsClassLoading(true);
    httprequest
      .get(CLASS_ENDPOINT)
      .then((res) => {
        setClassData(res?.data?.data);
        setIsClassLoading(false);
      })
      .catch((err) => {
        setIsClassLoading(false);
        toast.error(err?.response?.data);
      });
  };

  /**
 * Fetches all periods with optional query parameters.
 *
 * @param {object} params - The query parameters for the request.
 */
  const getAllPeriods = (params) => {
    httprequest
      .get(`${TEACHER_PERIOD}`, { params })
      .then((res) => {
        setPeriodData(res?.data);
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };
  // Mapping day abbreviations to actual dates
  const startDate = new Date(activeTerm?.start_date);
  const EndDate = new Date(activeTerm?.end_date);

  /**
 * Generates calendar events from the period data within the active term date range.
 *
 * @returns {Array<Object>} Array of event objects for calendar rendering.
 */
  const generateEvents = () => {
    const allEvents = [];
    const currentDate = new Date(startDate);

    while (currentDate <= EndDate) {
      const weekDay = currentDate
      // Get day and convert to lowercase abbreviat
        .toLocaleDateString("en-US", { weekday: "short" })
        .toLowerCase();

      periodData?.data?.days.forEach((day) => {
        if (weekDay === day?.day) {
          day?.periods.forEach((periodItem) => {
            const event = {
              type: periodItem?.type,
              title: periodItem?.subject?.name || periodItem?.title,
              start: `${currentDate.toISOString().split("T")[0]}T${periodItem?.start_time}`,
              end: `${currentDate.toISOString().split("T")[0]}T${periodItem?.end_time}`,
              teacher: periodItem?.teachers?.[0]?.name,
              subject_name: periodItem?.subject?.name,
              subject_id: periodItem?.subject?.id,
              day: day?.day,
              class: periodData?.data?.class?.name,
              class_id: periodData?.data?.class?.id,
              term: periodData?.data?.term?.name,
              term_id: periodData?.data?.term?.id,
              period_id: periodItem?.id,
              className: "general-events"
            };
            allEvents.push(event);
          });
        }
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allEvents;
  };

  const events = generateEvents();

  /**
 * Renders content for each calendar event.
 *
 * @param {Object} eventInfo - Event information from the calendar.
 * @param {boolean} isMobile - Whether the device is mobile.
 * @returns {JSX.Element} Rendered event content.
 */
  const renderEventContent = (eventInfo, isMobile) => {
    return (
      <div className={isMobile ? "rotated-vertical" : "normal-horizontal"}>
        <p className="no-margin-block padding-inline-10">{eventInfo.event?.title} | {eventInfo?.event?.extendedProps?.class}</p>
        <p className="no-margin-block padding-inline-10">{eventInfo.event?.extendedProps?.teacher}</p>
      </div>
    );
  };

  /**
 * Handles tab change when switching between classes.
 * Updates selected class, fetches related subjects and periods.
 *
 * @param {event parameter passed on handleChange} event - The tab change event.
 * @param {string|number} newValue - The new class ID.
 */
  const handleChange = (event, newValue) => {
    setValue(newValue);
    const params = {
      class_id: newValue
    };
    getAllPeriods(params);
  };

  /**
 * Initializes the selected class value and fetches periods for the default class
 * when class data becomes available and no class is currently selected.
 * This effect runs only once when the component mounts or when classData changes.
 * @dependency classData
 */
  useEffect(() => {
    if (classData?.length > 0 && !value) {
      setValue(classData[0]?.class?.id);
      const params = {
        class_id: classData[0]?.class?.id
      };
      getAllPeriods(params);
    }
  }, [classData]);

  useEffect(() => {
    getAllClasses();
    getAllTerms();
  }, []);

  // useEffect(() => {
  //   if (selectedClassSubjects) {
  //     getAllSubjectForClass(selectedClassSubjects);
  //   }
  // }, [selectedClassSubjects]);

  /**
 * Sets the time slot configuration depending on the calendar view (mobile or not).
 * Adjusts min and max times shown on the calendar.
 * This effect runs whenever the mobile state changes.
 * @dependency mobile
 */
  useEffect(() => {
    if (renderView() === "timeGridDay") {
      setTimeConfig({
        slotMinTime: "07:00:00",
        slotMaxTime: "19:00:00"
      });
    } else {
      setTimeConfig({
        slotMinTime: "07:00:00",
        slotMaxTime: "19:00:00"
      });
    }
  }, [mobile]);

  return (
    <>
      <PageTitle
        title="My Timetable"
        subtitle="See all timetable for the Classes I Teach"
        button={
          <div className="flex gap-10">
            <PopModal videoUrl="https://youtu.be/g3qI35QZuS8" title="Time Table" modalKey="timetable" />
            {/* <Button
              variant="primaryoutline"
              buttonSize="fluid"
              color="btnfontprimary"
              buttonName="Create Timetable"
              onClick={handleTimeTableModal}
            /> */}
            {/* <Button
              variant="primary"
              buttonSize="fluid"
              color="btndefault"
              buttonName="Update Timetable"
              onClick={() => navigate("/time-table/subject-timetable")}
            /> */}
          </div>
        }
      />
      <div className="page-content">
        <div className="padding-left-16">
          <div className={`${classes.terminfo}`}>
            <div className="flex gap-10 justify-content-between">
              <p className="text-align-left">Current Term: </p>
              <p>{activeTerm?.term}</p>
            </div>
            <div className="flex gap-10 justify-content-between">
              <p>Starts On: </p>
              <p>{activeTerm?.start_date?.substring(0, 10)}</p>
            </div>
            <div className="flex gap-10 justify-content-between">
              <p>Ends on: </p>
              <p>{activeTerm?.end_date?.substring(0, 10)}</p>
            </div>
          </div>
        </div>
        <TabContext value={value}>
          <div className="tablist-container padding-left-16">
            {isClassLoading ? (
              <div className="container-left">
                <Icon icon="eos-icons:three-dots-loading" width="50" height="50" />
              </div>
            ) : (
              <Tabs
                className="overide-tablist"
                onChange={handleChange}
                aria-label="class tabs"
              >
                {classData?.map((data) => {
                  return (
                    <Tab
                      key={data?.class?.id}
                      value={data?.class?.id}
                      label={data?.class?.name}
                    />
                  );
                })}
              </Tabs>
            )}
          </div>
          {classData?.map((data) => {
            return (
              <TabPanel sx={{ padding: "0px !important" }} key={data?.class?.id} value={data?.class?.id}>
                <div className={classes.calender}>
                  <Calendar
                    events={events}
                    renderEventContent={(eventInfo) => renderEventContent(eventInfo, mobile)}
                    defaultView={renderView()}
                    minTime={timeConfig?.slotMinTime}
                    maxTime={timeConfig?.slotMaxTime}
                    handleEventClick={false}
                  />
                </div>
              </TabPanel>
            );
          })}
        </TabContext>
      </div>
      {/* <ToastContainer /> */}
    </>
  );
};

export default AdminTimeTable;
